import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import {
  Loader2, LogOut, Plus, Trash2, Edit3, Save, X as XIcon, ArrowLeft,
} from "lucide-react";
import {
  adminVerify, fetchRecipes, createRecipe, updateRecipe, deleteRecipe,
} from "../lib/api";
import { PRODUCTS } from "../lib/brand";
import { Navigation } from "../components/landing/Navigation";
import { ImageUploader } from "../components/admin/ImageUploader";

const EMPTY_FORM = {
  slug: "",
  name: "",
  kicker: "",
  prep: "",
  cook: "",
  serves: "",
  heat: 0,
  spice_slug: PRODUCTS[0]?.slug || "haldi",
  spice_name: PRODUCTS[0]?.name || "Haldi Powder",
  image: "",
  intro: "",
  ingredients: "",
  steps: "",
  tips: "",
};

const slugify = (s) =>
  s.toLowerCase().trim().replace(/[^a-z0-9\s-]/g, "").replace(/\s+/g, "-").slice(0, 60);

const toPayload = (form) => ({
  ...form,
  heat: Number(form.heat) || 0,
  ingredients: String(form.ingredients || "")
    .split("\n")
    .map((l) => l.trim())
    .filter(Boolean),
  steps: String(form.steps || "")
    .split("\n")
    .map((l) => l.trim())
    .filter(Boolean),
});

const fromRecipe = (r) => ({
  ...EMPTY_FORM,
  ...r,
  ingredients: (r.ingredients || []).join("\n"),
  steps: (r.steps || []).join("\n"),
});

const AdminRecipes = () => {
  const [token, setToken] = useState(() => localStorage.getItem("maati_admin_token") || "");
  const [input, setInput] = useState("");
  const [authed, setAuthed] = useState(false);
  const [authLoading, setAuthLoading] = useState(false);

  const [recipes, setRecipes] = useState([]);
  const [listLoading, setListLoading] = useState(false);

  const [editing, setEditing] = useState(null); // null | 'new' | slug
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  // Auto-try existing token
  useEffect(() => {
    if (!token) return;
    adminVerify(token)
      .then(() => setAuthed(true))
      .catch(() => {
        localStorage.removeItem("maati_admin_token");
        setToken("");
      });
  }, [token]);

  const loadRecipes = async () => {
    setListLoading(true);
    try {
      const data = await fetchRecipes();
      setRecipes(data);
    } catch {
      toast.error("Couldn't load recipes.");
    } finally {
      setListLoading(false);
    }
  };

  useEffect(() => {
    if (authed) loadRecipes();
  }, [authed]);

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!input) return;
    setAuthLoading(true);
    try {
      await adminVerify(input);
      localStorage.setItem("maati_admin_token", input);
      setToken(input);
      setAuthed(true);
      toast.success("Welcome, chef.");
    } catch {
      toast.error("Invalid admin token.");
    } finally {
      setAuthLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("maati_admin_token");
    setToken("");
    setAuthed(false);
    setEditing(null);
  };

  const startNew = () => {
    setForm(EMPTY_FORM);
    setEditing("new");
  };

  const startEdit = (r) => {
    setForm(fromRecipe(r));
    setEditing(r.slug);
  };

  const cancelEdit = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
  };

  const onChange = (k) => (e) => {
    const value = e.target.type === "number" ? Number(e.target.value) : e.target.value;
    setForm((f) => {
      const next = { ...f, [k]: value };
      if (k === "spice_slug") {
        const p = PRODUCTS.find((pp) => pp.slug === value);
        if (p) next.spice_name = p.name;
      }
      if (k === "name" && editing === "new" && !f.slug) {
        next.slug = slugify(value);
      }
      return next;
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = toPayload(form);
      if (!payload.slug || !payload.name || !payload.image) {
        toast.error("Slug, name and image URL are required.");
        setSaving(false);
        return;
      }
      if (editing === "new") {
        await createRecipe(payload);
        toast.success(`"${payload.name}" published.`);
      } else {
        await updateRecipe(editing, payload);
        toast.success("Recipe updated.");
      }
      await loadRecipes();
      cancelEdit();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async (slug) => {
    if (!window.confirm(`Delete recipe "${slug}"? This cannot be undone.`)) return;
    try {
      await deleteRecipe(slug);
      toast.success("Recipe removed.");
      await loadRecipes();
    } catch {
      toast.error("Delete failed.");
    }
  };

  if (!authed) {
    return (
      <main className="min-h-screen bg-mo-cream">
        <Navigation />
        <div className="max-w-md mx-auto px-6 pt-40 pb-20">
          <div className="text-xs uppercase tracking-[0.3em] text-mo-terracotta mb-4">
            — Admin · Recipes
          </div>
          <h1 className="font-serif-display text-5xl text-mo-ink leading-tight">
            The kitchen door.
          </h1>
          <p className="mt-4 text-mo-ink-soft">
            Enter the admin token to add, edit or retire recipes.
          </p>
          <form onSubmit={handleLogin} className="mt-10 space-y-4" data-testid="admin-login-form">
            <label className="block">
              <span className="block text-xs uppercase tracking-[0.2em] text-mo-ink-soft mb-2">
                Admin token
              </span>
              <input
                type="password"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                data-testid="admin-token-input"
                className="w-full px-4 py-3 rounded-lg border border-[#5C5248]/30 bg-[#F9F6F0] text-mo-ink focus:outline-none focus:ring-2 focus:ring-[#B33922]/40"
                placeholder="••••••••"
              />
            </label>
            <button
              type="submit"
              disabled={authLoading}
              data-testid="admin-login-submit"
              className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-mo-terracotta text-mo-cream font-medium hover:bg-[#8F2B17] transition-colors disabled:opacity-60"
            >
              {authLoading ? <Loader2 className="animate-spin" size={16} /> : null}
              Unlock
            </button>
          </form>
          <Link to="/" className="mt-10 inline-flex items-center gap-2 text-sm text-mo-ink-soft hover:text-mo-terracotta">
            <ArrowLeft size={14} /> Back to site
          </Link>
        </div>
      </main>
    );
  }

  return (
    <main data-testid="admin-recipes-page" className="min-h-screen bg-mo-cream">
      <Navigation />
      <div className="max-w-6xl mx-auto px-6 lg:px-10 pt-32 pb-20">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-10">
          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-mo-terracotta mb-3">
              — Admin · Recipes
            </div>
            <h1 className="font-serif-display text-5xl text-mo-ink leading-tight">
              The Maati Kitchen, <span className="italic text-mo-green">backstage.</span>
            </h1>
          </div>
          <div className="flex gap-3">
            <button
              onClick={startNew}
              data-testid="admin-new-recipe"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-mo-terracotta text-mo-cream text-sm hover:bg-[#8F2B17] transition-colors"
            >
              <Plus size={15} /> New recipe
            </button>
            <button
              onClick={handleLogout}
              data-testid="admin-logout"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-[#5C5248]/30 text-mo-ink-soft text-sm hover:border-mo-terracotta hover:text-mo-terracotta transition-colors"
            >
              <LogOut size={15} /> Log out
            </button>
          </div>
        </div>

        {editing && (
          <RecipeForm
            form={form}
            onChange={onChange}
            onCancel={cancelEdit}
            onSubmit={submit}
            saving={saving}
            isNew={editing === "new"}
          />
        )}

        {/* List */}
        <div className="mt-10">
          <div className="text-xs uppercase tracking-[0.2em] text-mo-ink-soft mb-4">
            {listLoading ? "Loading…" : `${recipes.length} recipes`}
          </div>
          <div className="space-y-3">
            {recipes.map((r) => (
              <div
                key={r.slug}
                data-testid={`admin-row-${r.slug}`}
                className="flex items-center gap-4 p-3 bg-[#F9F6F0] border border-[#5C5248]/12 rounded-xl"
              >
                <img src={r.image} alt="" className="w-16 h-16 object-cover rounded-lg" />
                <div className="flex-1 min-w-0">
                  <div className="font-serif-display text-xl text-mo-ink truncate">
                    {r.name}
                  </div>
                  <div className="text-xs text-mo-ink-soft truncate">
                    {r.spice_name} · {r.kicker} · /recipes/{r.slug}
                  </div>
                </div>
                <div className="flex gap-2 shrink-0">
                  <Link
                    to={`/recipes/${r.slug}`}
                    className="text-xs px-3 py-2 rounded-full border border-[#5C5248]/20 text-mo-ink-soft hover:border-mo-terracotta hover:text-mo-terracotta"
                    target="_blank"
                  >
                    View
                  </Link>
                  <button
                    onClick={() => startEdit(r)}
                    data-testid={`admin-edit-${r.slug}`}
                    className="inline-flex items-center gap-1.5 text-xs px-3 py-2 rounded-full border border-[#5C5248]/20 text-mo-ink-soft hover:border-mo-terracotta hover:text-mo-terracotta"
                  >
                    <Edit3 size={13} /> Edit
                  </button>
                  <button
                    onClick={() => onDelete(r.slug)}
                    data-testid={`admin-delete-${r.slug}`}
                    className="inline-flex items-center gap-1.5 text-xs px-3 py-2 rounded-full border border-[#B33922]/40 text-mo-terracotta hover:bg-[#B33922] hover:text-mo-cream"
                  >
                    <Trash2 size={13} /> Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </main>
  );
};

const RecipeForm = ({ form, onChange, onCancel, onSubmit, saving, isNew }) => (
  <form
    onSubmit={onSubmit}
    data-testid="admin-recipe-form"
    className="bg-[#EAE3D9]/70 rounded-3xl p-6 sm:p-8 border border-[#5C5248]/12 kraft-grain"
  >
    <div className="flex items-center justify-between mb-6">
      <h2 className="font-serif-display text-2xl text-mo-ink">
        {isNew ? "New recipe" : `Editing · ${form.name}`}
      </h2>
      <button
        type="button"
        onClick={onCancel}
        className="p-2 text-mo-ink-soft hover:text-mo-terracotta"
        aria-label="Close"
      >
        <XIcon size={18} />
      </button>
    </div>

    <div className="grid sm:grid-cols-2 gap-4">
      <Text label="Name" value={form.name} onChange={onChange("name")} required testId="form-name" />
      <Text label="Slug (URL)" value={form.slug} onChange={onChange("slug")} required disabled={!isNew} testId="form-slug" />
      <Text label="Kicker" value={form.kicker} onChange={onChange("kicker")} placeholder="Weeknight · 20 min" testId="form-kicker" />
      <Text label="Prep" value={form.prep} onChange={onChange("prep")} placeholder="10 min" testId="form-prep" />
      <Text label="Cook" value={form.cook} onChange={onChange("cook")} placeholder="20 min" testId="form-cook" />
      <Text label="Serves" value={form.serves} onChange={onChange("serves")} placeholder="3" testId="form-serves" />
      <label className="block">
        <span className="block text-xs uppercase tracking-[0.2em] text-mo-ink-soft mb-2">
          Spice
        </span>
        <select
          value={form.spice_slug}
          onChange={onChange("spice_slug")}
          data-testid="form-spice"
          className="w-full px-4 py-3 rounded-lg border border-[#5C5248]/30 bg-[#F9F6F0] text-mo-ink"
        >
          {PRODUCTS.map((p) => (
            <option key={p.slug} value={p.slug}>
              {p.name}
            </option>
          ))}
        </select>
      </label>
      <label className="block">
        <span className="block text-xs uppercase tracking-[0.2em] text-mo-ink-soft mb-2">
          Heat (0–3)
        </span>
        <input
          type="number"
          min={0}
          max={3}
          value={form.heat}
          onChange={onChange("heat")}
          data-testid="form-heat"
          className="w-full px-4 py-3 rounded-lg border border-[#5C5248]/30 bg-[#F9F6F0] text-mo-ink"
        />
      </label>
    </div>

    <div className="mt-4">
      <ImageUploader
        value={form.image}
        onChange={(url) => onChange("image")({ target: { value: url } })}
        label="Recipe image (drag & drop or browse)"
      />
    </div>

    <div className="mt-4">
      <Textarea
        label="Intro"
        rows={3}
        value={form.intro}
        onChange={onChange("intro")}
        testId="form-intro"
      />
    </div>

    <div className="mt-4 grid sm:grid-cols-2 gap-4">
      <Textarea
        label="Ingredients (one per line)"
        rows={7}
        value={form.ingredients}
        onChange={onChange("ingredients")}
        testId="form-ingredients"
      />
      <Textarea
        label="Steps (one per line)"
        rows={7}
        value={form.steps}
        onChange={onChange("steps")}
        testId="form-steps"
      />
    </div>

    <div className="mt-4">
      <Textarea
        label="Chef's tip"
        rows={2}
        value={form.tips}
        onChange={onChange("tips")}
        testId="form-tips"
      />
    </div>

    <div className="mt-8 flex gap-3">
      <button
        type="submit"
        disabled={saving}
        data-testid="form-save"
        className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-mo-terracotta text-mo-cream hover:bg-[#8F2B17] transition-colors disabled:opacity-60"
      >
        {saving ? <Loader2 className="animate-spin" size={15} /> : <Save size={15} />}
        {isNew ? "Publish recipe" : "Save changes"}
      </button>
      <button
        type="button"
        onClick={onCancel}
        className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-[#5C5248]/25 text-mo-ink-soft hover:text-mo-ink"
      >
        Cancel
      </button>
    </div>
  </form>
);

const Text = ({ label, testId, ...props }) => (
  <label className="block">
    <span className="block text-xs uppercase tracking-[0.2em] text-mo-ink-soft mb-2">
      {label}
    </span>
    <input
      {...props}
      data-testid={testId}
      className="w-full px-4 py-3 rounded-lg border border-[#5C5248]/30 bg-[#F9F6F0] text-mo-ink focus:outline-none focus:ring-2 focus:ring-[#B33922]/40 disabled:opacity-60"
    />
  </label>
);

const Textarea = ({ label, testId, ...props }) => (
  <label className="block">
    <span className="block text-xs uppercase tracking-[0.2em] text-mo-ink-soft mb-2">
      {label}
    </span>
    <textarea
      {...props}
      data-testid={testId}
      className="w-full px-4 py-3 rounded-lg border border-[#5C5248]/30 bg-[#F9F6F0] text-mo-ink focus:outline-none focus:ring-2 focus:ring-[#B33922]/40 resize-none"
    />
  </label>
);

export default AdminRecipes;
